import { toaster } from '@/components/ui/toaster';
import axiosInstance from '@/libs/axiosInstance';
import { ErrorCode } from '@fluenceur/types';
import type { ApiErrorResponse, ApiResponse, PayoutBatchStatus } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { isPayoutBatchConflict } from './payout-batch-conflict';

interface CancelPayoutResponse {
  id: string;
  status: PayoutBatchStatus;
}

interface UseCancelPayoutActionOptions {
  onSuccess?: (data: CancelPayoutResponse) => void;
  onError?: (error: Error) => void;
}

export const useCancelPayoutAction = (options?: UseCancelPayoutActionOptions) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const getErrorMessage = (error: AxiosError<ApiErrorResponse>): string => {
    const errorCode = error.response?.data?.code;

    // Try to translate based on error code
    if (errorCode) {
      const translationKey = `errors.${errorCode}`;
      const translated = t(translationKey, { defaultValue: '' });
      if (translated && translated !== translationKey) {
        return translated;
      }
    }

    // Map specific error codes to user-friendly messages
    switch (errorCode) {
      case ErrorCode.E_PAYOUT_BATCH_NOT_FOUND:
        return t('payouts.cancelBatch.error.notFound');
      case ErrorCode.E_PAYOUT_BATCH_NOT_CANCELLABLE:
        return t('payouts.cancelBatch.error.notCancellable');
      default:
        // Fall back to API message or generic error
        return error.response?.data?.message || t('payouts.cancelBatch.error.generic');
    }
  };

  const mutation = useMutation({
    mutationFn: async (batchId: string) => {
      const response = await axiosInstance.delete<ApiResponse<CancelPayoutResponse>>(
        `/dashboard/payouts/${batchId}`
      );
      return response.data;
    },
    onSuccess: (response) => {
      // Invalidate the payouts list query to refetch
      queryClient.invalidateQueries({ queryKey: ['payoutBatches'] });
      // Invalidate commission stats since cancelled commissions return to pending
      queryClient.invalidateQueries({ queryKey: ['commissions', 'stats'] });

      toaster.create({
        title: t('payouts.cancelBatch.success.title'),
        description: t('payouts.cancelBatch.success.description'),
        type: 'success',
        duration: 5000,
      });

      options?.onSuccess?.(response.data as CancelPayoutResponse);
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      console.error('Error cancelling payout batch:', error);

      const errorMessage = getErrorMessage(error);

      // Paid, being paid or closed since the list was read: the row is stale.
      if (isPayoutBatchConflict(error)) {
        queryClient.invalidateQueries({ queryKey: ['payoutBatches'] });
      }

      toaster.create({
        title: t('payouts.cancelBatch.error.title'),
        description: errorMessage,
        type: 'error',
        duration: 5000,
      });

      options?.onError?.(error);
    },
  });

  return {
    cancelPayout: mutation.mutate,
    cancelPayoutAsync: mutation.mutateAsync,
    isCancelling: mutation.isPending,
  };
};
